import {
    FrameCollectionModule,
    Logger,
    LogLevels,
} from 'UI-Dash-Commons-UI';

const TAG = 'FrameMessages';

type MessageProcessor =
    {
        ( Message ): void,
    };

const messageProcessors: { [ s: string ]: Array<MessageProcessor> } = {};

const frames: { [ s: string ]: Window } = {};

let isListenerAttached = false;

const FrameMessages = {

    attach: () =>
    {

        if ( isListenerAttached )
        {

            Logger.error( TAG, `Attempted to re-attach message listener to window...` );
            return;
        }

        Logger.debug( TAG, `Attaching message listener to window...` );
        window.addEventListener( 'message', FrameMessages.receiveMessage, false );

        isListenerAttached = true;
    },

    detach: () =>
    {

        if ( !isListenerAttached )
        {

            Logger.debug( TAG, `Attempted to detach message listener from window when listener is not attached...` );
            return;
        }

        Logger.debug( TAG, `Removing message listener from window...` );
        window.removeEventListener( 'message', FrameMessages.receiveMessage, false );

        isListenerAttached = false;
    },

    registerFrame: ( frameTag: string, frame: Window ) =>
    {

        if ( !FrameMessages.isValidFrameTag( frameTag, LogLevels.ERROR ) )
        {

            return false;
        }

        if ( !frame )
        {
            Logger.error( TAG, `Attempting to register invalid frame for ${frameTag}...` );
            return false;
        }

        Logger.debug( TAG, `Frame registered for ${frameTag}...` );
        frames[ frameTag ] = frame;

        return true;
    },

    removeRegisteredFrame: ( frameTag: string ) =>
    {

        if ( !FrameMessages.isValidFrameTag( frameTag, LogLevels.DEBUG ) )
        {

            return false;
        }

        Logger.debug( TAG, `Removing registered frame ${frameTag}...` );
        delete frames[ frameTag ];

        return true;
    },

    isValidFrameTag: ( frameTag: string, logLevel: LogLevels ) =>
    {

        return FrameMessages.isString( frameTag, 'Invalid frame tag, string expected...', logLevel );
    },

    registerMessageProcessor: ( messageType: string, messageProcessor: MessageProcessor ) =>
    {

        if ( !FrameMessages.isValidMessageTypeAndMessageProcessor( messageType, messageProcessor, LogLevels.ERROR ) )
        {

            return false;
        }

        Logger.debug( TAG, `Message processor registered for type: ${messageType}...` );
        if ( typeof( messageProcessors[ messageType ] ) === 'undefined' )
        {

            messageProcessors[ messageType ] = [];
        }

        messageProcessors[ messageType ].push( messageProcessor );

        return true;
    },

    removeRegisteredMessageProcessor: ( messageType: string, messageProcessor: MessageProcessor ) =>
    {

        if ( !FrameMessages.isValidMessageTypeAndMessageProcessor( messageType, messageProcessor, LogLevels.DEBUG ) )
        {

            return false;
        }

        Logger.debug( TAG, `Removing message processor for: ${messageType}...` );
        messageProcessors[ messageType ].filter( currentProcessor => currentProcessor !== messageProcessor );

        return true;
    },

    isValidMessageTypeAndMessageProcessor: ( messageType: string, messageProcessor: MessageProcessor, logLevel: LogLevels ) =>
    {

        return FrameMessages.isValidMessageType( messageType, logLevel )
            && FrameMessages.isValidMessageProcessor( messageProcessor, logLevel );
    },

    isValidMessageProcessor: ( messageProcessor: MessageProcessor, logLevel: LogLevels ) =>
    {

        const valid = typeof( messageProcessor ) === 'function';

        if ( !valid )
        {

            Logger.log( TAG, 'Invalid message processor, function expected...', logLevel );
        }

        return valid;
    },

    receiveMessage: ( event: MessageEvent ) =>
    {
        if ( !FrameMessages.isValidMessage( event ) )
        {
            return;
        }

        const sourceWindow: Window = ( event.source as Window );
        const frameTag = FrameMessages.findFrameTag( sourceWindow );
        FrameMessages.processMessage( frameTag, event.data );
    },

    isValidMessage: ( event: MessageEvent ) =>
    {

        // Check event origin
        if ( event.origin !== window.origin )
        {

            Logger.error( TAG, `Received message from foreign origin: '${event.origin}', expected '${window.origin}` );
            return false;
        }

        // Detect source frame
        const sourceWindow: Window = ( event.source as Window );

        if ( !FrameMessages.isFrameRegistered( sourceWindow ) && (event.data && event.data.type !== 'show_notification') )
        {
            Logger.debug( TAG, `Ignored message from not registered frame:  '${sourceWindow.location}': (${JSON.stringify( event.data )})` );
            return false;
        }

        const message = event.data;
        if ( !Object.prototype.hasOwnProperty.call( message, 'type' ) )
        {

            Logger.error( TAG, `Invalid message: 'type' field is missing...` );
            return false;
        }

        return FrameMessages.isValidMessageType( message.type, LogLevels.ERROR );
    },

    isValidMessageType: ( messageType: string, logLevel: LogLevels ) =>
    {

        return FrameMessages.isString( messageType, 'Message type is not a string', logLevel );
    },

    isString: ( variable: any, logMessage: string, logLevel: LogLevels ) =>
    {

        const valid = typeof( variable ) === 'string';

        if ( !valid )
        {

            Logger.log( TAG, logMessage, logLevel );
        }

        return valid;
    },

    isFrameRegistered: ( frame: Window | null ) =>
    {

        return FrameMessages.findFrameTag( frame ) !== null;
    },

    findFrameTag: ( frame: Window | null ) =>
    {

        for ( const frameTag in frames )
        {

            if ( !frames.hasOwnProperty( frameTag ) )
            {

                continue;
            }

            if ( frame === frames[ frameTag ] )
            {

                return frameTag;
            }
        }

        return null;
    },

    processMessage: ( frameTag: string | null, message: FrameCollectionModule.types.Message ) =>
    {

        if ( !messageProcessors[ message.type ] )
        {

            return;
        }

        Logger.info( TAG, `Processing ${message.type} message from frame:  '${frameTag}'...` );

        messageProcessors[ message.type ].forEach( processor => processor( message ) );
    }
};

const FrameMessagesAPI = {

    startListening: () =>
    {

        FrameMessages.attach();
    },

    stopListening: () =>
    {

        FrameMessages.detach();
    },

    registerFrame: ( frameTag: string, frame: Window ) =>
    {

        return FrameMessages.registerFrame( frameTag, frame );
    },

    removeRegisteredFrame: ( frameTag: string ) =>
    {

        return FrameMessages.removeRegisteredFrame( frameTag );
    },

    registerMessageProcessor: ( messageType: string, messageProcessor: MessageProcessor ) =>
    {

        return FrameMessages.registerMessageProcessor( messageType, messageProcessor );
    },

    removeRegisteredMessageProcessor: ( messageType: string, messageProcessor: MessageProcessor ) =>
    {

        return FrameMessages.removeRegisteredMessageProcessor( messageType, messageProcessor );
    }
};

export default FrameMessagesAPI;
